import { useContext, useEffect, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
  Stack,
  Tooltip,
  Box,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
// Imports from project
import "../../Style/dialogstyle.scss";
import EmptyImage from "../../Assets/empty-image.png";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import useLessonForm from "./Hooks/useOnlineBookForm";
import { SelectBookGroupFee } from "../Include/DynamicSelect/DynamicSelect";
import { useDeleteImageServer } from "../CropImage/DeleteImageServer";
import { UploadImageNoCrop } from "../CropImage/UploadImageNoCrop";
import LoadingProgess from "../../Utils/LoadingProgress";

export default function OnlineBookForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop = false,
}) {
  const { t } = useContext(AuthContext);

  // =================================== Upload Image ====================================
  const mutationDelete = useDeleteImageServer();
  const [profileHook, setProfileHook] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [statusProgress, setStatusProgress] = useState(false);
  const [progress, setProgress] = useState(10);
  // console.log("progress::", progress)

  const handleUploadImage = async (e) => {
    const imageFile = e.target.files[0];
    setStatusProgress(true);
    if (imageFile) {
      setImageFile(imageFile);
      const uploadedImg = await UploadImageNoCrop(imageFile);
      setProfileHook(uploadedImg);
    }
  };

  const handleCloseAction = () => {
    handleClose();
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      mutationDelete.mutate({
        storage: "sr_music_academy",
        folder: "sr_music_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };

  // ================================= End upload Image ===============================

  const [keywordInput, setKeywordInput] = useState("");

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const keyword = keywordInput.trim();

      if (!keyword) return;

      const currentKeywords = values?.keywords || [];
      // console.log("currentKeywords::", currentKeywords)

      // Prevent duplicate keywords
      if (!currentKeywords.includes(keyword)) {
        setFieldValue("keywords", [...currentKeywords, keyword]);
      }

      setKeywordInput("");
    }

    // Optional: remove last keyword when pressing Backspace
    if (e.key === "Backspace" && !keywordInput && values?.keywords?.length) {
      setFieldValue("keywords", values.keywords.slice(0, -1));
    }
  };

  const handleDeleteKeyword = (keywordToDelete) => {
    setFieldValue(
      "keywords",
      values.keywords.filter((keyword) => keyword !== keywordToDelete),
    );
  };

  // ============================== Formik & Validation ===============================
  const CheckValidation = Yup.object().shape({
    title: Yup.string().required(t("required") || "Required"),
    groupFeeId: Yup.object().shape({
      id: Yup.string().required(t("required") || "Required"),
      title: Yup.string(),
    }),
    numbering: Yup.number().required(t("required") || "Required"),
    description: Yup.string(),
    author: Yup.string(),
    type: Yup.string(),
    discountType: Yup.string(),
    discountAmount: Yup.number(),
    payStatus: Yup.string(),
    rating: Yup.number(),
    pages: Yup.number(),
    image: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      groupFeeId: { id: "", title: "" },
      numbering: 1,
      description: "",
      author: "",
      type: "NoteMelody",
      payStatus: "pay",
      discountType: "amount",
      discountAmount: 0,
      rating: 0,
      pages: 0,
      image: "",
      keywords: [],
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);

      // console.log("InputLesson::", keywordInput);
      // console.log("profileHook::", profileHook);

      if (dialogTitle === "Create") {
        createOnlineBook({
          variables: {
            input: {
              title: values.title,
              groupFeeId: values.groupFeeId.id,
              numbering: values.numbering,
              description: values.description || "",
              image: profileHook,
              author: values?.author,
              type: values?.type,
              rating: values?.rating,
              pages: values?.pages,
              payStatus: values?.payStatus,
              discountType: values?.discountType,
              discountAmount: values?.discountAmount,
              keywords: values.keywords,
            },
          },
        });
      } else {
        updateOnlineBook({
          variables: {
            id: editData?._id,
            input: {
              title: values.title,
              groupFeeId: values.groupFeeId.id,
              numbering: values.numbering,
              description: values.description || "",
              image: profileHook ? profileHook : editData?.image,
              author: values?.author,
              type: values?.type,
              rating: values?.rating,
              pages: values?.pages,
              payStatus: values?.payStatus,
              discountType: values?.discountType,
              discountAmount: values?.discountAmount,
              keywords: values.keywords,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    handleChange,
  } = formik;

  // console.log("values::", values.keywords)

  const { createOnlineBook, loading, updateOnlineBook, setLoading } =
    useLessonForm({
      mutationDelete,
      editData,
      profileHook,
      resetForm,
      setRefetch,
      handleClose,
    });

  useEffect(() => {
    if (open) {
      if (dialogTitle === "Update" && editData) {
        setFieldValue("title", editData?.title || "");
        setFieldValue(
          "groupFeeId.id",
          editData?.groupFeeId?._id || editData?.groupFeeId?.id || "",
        );
        setFieldValue(
          "groupFeeId.title",
          `${editData?.groupFeeId?.title}: ${editData?.groupFeeId?.price} $` ||
            "",
        );
        setFieldValue("type", editData?.type || "");
        setFieldValue("author", editData?.author || "");
        setFieldValue("pages", editData?.pages || "");
        setFieldValue("rating", editData?.rating || "");
        setFieldValue("numbering", editData?.numbering);
        setFieldValue("description", editData?.description || "");
        setFieldValue("payStatus", editData?.payStatus);
        setFieldValue("discountType", editData?.discountType);
        setFieldValue("discountAmount", editData?.discountAmount);
        setFieldValue("keywords", editData?.keywords);
      } else {
        resetForm();
      }
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="md"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_book")
            : t("modal_title_update_book")
        }
        classTitle="dialog-title"
        classIcon="close-icon"
        handleClose={handleCloseAction}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_numbering")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  type="number"
                  fullWidth
                  name="numbering"
                  value={values?.numbering || ""}
                  onChange={handleChange}
                  placeholder={t("thead_numbering") || "Numbering"}
                  error={Boolean(touched.numbering && errors.numbering)}
                  helperText={touched.numbering && errors.numbering}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <SelectBookGroupFee
                  value={values?.groupFeeId}
                  onChange={(val) => {
                    if (val) {
                      setFieldValue("groupFeeId", val);
                    } else {
                      setFieldValue("groupFeeId.id", "");
                      setFieldValue("groupFeeId.title", "");
                    }
                  }}
                />
                {Boolean(errors.groupFeeId?.id && touched.groupFeeId?.id) && (
                  <FormHelperText error id="content-id-error">
                    {errors.groupFeeId?.id}
                  </FormHelperText>
                )}
              </Grid>

              {/* Image Preview / Upload */}
              <Grid item xs={4}>
                <Box>
                  <Stack direction="row" justifyContent="center">
                    <Tooltip title="click to upload">
                      <Button component="label">
                        <TextField
                          className="text-field"
                          type="file"
                          id="image"
                          sx={{ display: "none" }}
                          onChange={handleUploadImage}
                        />
                        <img
                          className="book-image"
                          alt=""
                          src={
                            imageFile
                              ? URL.createObjectURL(imageFile)
                              : editData?.image
                                ? editData?.image
                                : EmptyImage
                          }
                        />
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>

                {statusProgress && progress < 100 ? (
                  <LoadingProgess
                    progress={progress}
                    setProgress={setProgress}
                  />
                ) : (
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ textAlign: "center", marginTop: "5px" }}
                  >
                    {t("thead_image")}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={8}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_keyword")}
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "6px 8px",
                    minHeight: "200px",
                    "&:focus-within": {
                      borderColor: "#1976d2",
                      borderWidth: "2px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      alignItems: "center",
                    }}
                  >
                    {values?.keywords?.map((keyword, index) => (
                      <Chip
                        key={`${keyword}-${index}`}
                        label={keyword}
                        size="small"
                        onDelete={() => handleDeleteKeyword(keyword)}
                      />
                    ))}

                    <TextField
                      variant="standard"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={handleKeywordKeyDown}
                      placeholder={
                        values?.keywords?.length
                          ? ""
                          : t("thead_keyword") || "Enter keywords"
                      }
                      InputProps={{
                        disableUnderline: true,
                      }}
                      sx={{
                        flex: 1,
                        minWidth: "150px",
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_title")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  name="title"
                  value={values?.title || ""}
                  onChange={handleChange}
                  placeholder={t("thead_title") || "title"}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_type")}
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={values?.type}
                    onChange={({ target }) =>
                      setFieldValue("type", target?.value)
                    }
                  >
                    <MenuItem value={"NoteMelody"}>Note Melody</MenuItem>
                    <MenuItem value={"NoteAccompaniment"}>
                      Note Accompaniment
                    </MenuItem>
                    <MenuItem value={"BeginnerGuitar"}>
                      Beginner Guitar
                    </MenuItem>
                    <MenuItem value={"GuitarSolo"}>Guitar Solo</MenuItem>
                    <MenuItem value={"GuitarFingerstyle"}>
                      Guitar Fingerstyle
                    </MenuItem>
                    <MenuItem value={"PianoBook"}>Piano Book</MenuItem>
                    <MenuItem value={"Lyricsandchord"}>
                      Lyrics and chord
                    </MenuItem>
                    <MenuItem value={"ChristianSong"}>Christian Song</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_author")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  name="author"
                  value={values?.author || ""}
                  onChange={handleChange}
                  placeholder={t("thead_author") || "Author"}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_pages")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  type="number"
                  name="pages"
                  value={values?.pages || ""}
                  onChange={handleChange}
                  placeholder={t("thead_pages") || "Pages"}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_rating")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  type="number"
                  fullWidth
                  name="rating"
                  value={values?.rating || ""}
                  inputProps={{
                    min: 0,
                    max: 10,
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (Number(value) >= 0 && Number(value) <= 10)
                    ) {
                      handleChange(e);
                    }
                  }}
                  placeholder={t("thead_rating") || "Rating"}
                />
              </Grid>

              {/* discountAmount */}
              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_pay_status")}
                </Typography>
                <FormControl fullWidth size="small" className={"text-field"}>
                  <Select
                    value={values.payStatus}
                    onChange={({ target }) =>
                      setFieldValue("payStatus", target?.value)
                    }
                  >
                    <MenuItem value={"pay"}>Pay</MenuItem>
                    <MenuItem value={"free"}>Free</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_discount_type")}
                </Typography>
                <FormControl fullWidth size="small" className={"text-field"}>
                  <Select
                    disabled={values?.payStatus === "free"}
                    value={values.discountType}
                    onChange={({ target }) =>
                      setFieldValue("discountType", target?.value)
                    }
                  >
                    <MenuItem value={"amount"}>Amount</MenuItem>
                    <MenuItem value={"percent"}>Percent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_discount_amount")}{" "}
                  {values.discountType === "amount" ? "$" : "%"}
                </Typography>
                <TextField
                  disabled={values?.payStatus === "free"}
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_discount_amount")}
                  type="number"
                  value={values?.discountAmount}
                  onChange={(e) => {
                    const inputVal = parseFloat(e.target.value);
                    if (inputVal <= 0 || inputVal > values?.groupFeeId?.price) {
                      setFieldValue("discountAmount", 0);
                    } else {
                      setFieldValue("discountAmount", inputVal);
                    }
                  }}
                  error={Boolean(
                    touched.discountAmount && errors.discountAmount,
                  )}
                  helperText={touched.discountAmount && errors.discountAmount}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_description")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  multiline
                  rows={2}
                  fullWidth
                  name="description"
                  value={values?.description || ""}
                  onChange={handleChange}
                  placeholder={t("thead_description") || "Description"}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                {loading ? (
                  <Button className="btn-create" fullWidth disabled>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    type="submit"
                    className="btn-create"
                    disabled={statusProgress && progress < 100 ? true : false}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
